import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTodos,
  Actualizar,
  Eliminar,
  Insertar,
} from "../../redux/tiposOrganizacionesSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import useSubmit from "../../Hooks/useSubmit";
import { OrderTable } from "../../Components/OrderTable";
import { getAdminOrgTypePage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaTiposOrganizaciones";
function TiposOrganizaciones(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminOrgTypePage, setAdminOrgTypePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const { submitAction } = useSubmit();

  const dispatch = useDispatch();
  let busqueda = useRef();

  const { tiposOrganizaciones } = useSelector(
    (state) => state.tiposOrganizaciones
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminOrgTypePage?.orgtype_table   ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "tipoOrganizacionId", sortable: true },
      { 
        label: table[1]?.label || "Nombre", 
        accessor: "tipoOrganizacionNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.tipoOrganizacionNombre] || item.tipoOrganizacionNombre}</td>
        ),
      },
      { label: table[2]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminOrgTypePage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setTituloModal(adminOrgTypePage?.subtitleT || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    reset({ tipoOrganizacionId: 0, tipoOrganizacionNombre: "" });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarOrganizaciones = () => {
    dispatch(ObtenerTodos());
  };

  const cargarOrganizacionesPorCaracter = () => {
    dispatch(ObtenerTodos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(Eliminar, itemSelected.tipoOrganizacionId, () => {
      closeRemoveModalEdit();
      cargarOrganizaciones();
    });
  };

  const onSubmit = async (data) => {
    submitAction(
      data.tipoOrganizacionId > 0 ? Actualizar : Insertar,
      data,
      () => {
        closeModalEdit();
        cargarOrganizaciones();
      }
    );
  };

  useEffect(() => {
    cargarOrganizaciones();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminOrgTypePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminOrgTypePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminOrgTypePage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasTiposOrganizaciones = tiposOrganizaciones && Array.isArray(tiposOrganizaciones) && tiposOrganizaciones.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasTiposOrganizaciones) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre"
      const translationPromises = tiposOrganizaciones.map(async (item) => {
        if (item.tipoOrganizacionNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.tipoOrganizacionNombre, locale);
          nombreTranslations[item.tipoOrganizacionNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [tiposOrganizaciones, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminOrgTypePage?.subtitleO || "Agregar");
              setValue("tipoOrganizacionNombre", "");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminOrgTypePage?.title || "Tipos de organizaciones"}</h2>
        </div>

        <div className="header-admin-bar-items ml-auto">
          <label hidden>Búsqueda</label>
          <input
            type="search"
            className="form-control"
            onInput={cargarOrganizacionesPorCaracter}
            placeholder={adminOrgTypePage?.search_input || "Búsqueda"}
            ref={busqueda}
          />
        </div>
      </div>

      <div className="table-container">
        {tiposOrganizaciones.length > 0 ? (
          <OrderTable
            {...{ columns }}
            data={tiposOrganizaciones}
            clase="table-adm"
            actions={[
              {
                icon: "",
                clase: "text-primary",
                style: { display: "contents" },
                event: (item) => showModalEdit(item),
              },
              {
                icon: "",
                clase: "delete",
                style: { display: "contents" },
                event: (item) => showRemoveModalEdit(item),
              },
            ]}
          />
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminOrgTypePage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <i className="material-icons important"></i>
            <label>{adminOrgTypePage?.orgtype || "Tipo de organización"}</label>
            <input
              className="form-control"
              name="tipoOrganizacionNombre"
              {...register("tipoOrganizacionNombre", {
                required: validation?.required_field || "Campo requerido",
              })}
            />
            {errors.tipoOrganizacionNombre && (
              <p className="error">{errors.tipoOrganizacionNombre.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminOrgTypePage?.subtitleTR || "Eliminar plantillla"}
        message={adminOrgTypePage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected.tipoOrganizacionNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminOrgTypePage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminOrgTypePage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default TiposOrganizaciones;
